import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box } from '@mui/material'
import { Dialog, DialogContent, DialogHeader } from '@carto/meridian-ds/components'
import { DataExplorerTable } from 'features/data-explorer/types'
import SubscriptionExportDialogContent from './SubscriptionExportDialogContent/SubscriptionExportDialogContent'
import { useAppDispatch, useAppSelector } from 'app/state'
import { fetchSubscriptionsRequest, getSubscriptionBySlug } from 'features/data-observatory/state/dataObservatorySlice'
import useAccountSpatialExtensionInfo from 'features/account/state/useAccountSpatialExtensionInfo'
import Loader from 'features/common/ui/utils/Loader'
import useExportingJobs from 'features/jobs/ui/useJobs/useExportingJobs'
import useSubscriptions from 'features/data-observatory/ui/Subscriptions/useSubscriptions'
import { RequestStatus } from 'features/common/types'
import { JobStatusType } from 'features/jobs/types'

export enum SubscriptionExportDialogViews {
  Form = 'form',
  SqlEditor = 'sqlEditor',
  JobRunning = 'jobRunning',
  JobFailed = 'jobFailed',
  DownloadLinks = 'downloadLinks'
}

interface SubscriptionExportDialogProps {
  open: boolean
  table: DataExplorerTable
  jobId?: string
  onClose: () => void
}

function SubscriptionExportDialog({ open, table, jobId, onClose }: SubscriptionExportDialogProps) {
  const dispatch = useAppDispatch()

  const subscription = useAppSelector((state) => getSubscriptionBySlug(state, table.slug || ''))

  const { exportingJobs, currentExportingJob, clearCurrentExportingJob } = useExportingJobs()
  const { subscriptionsStatus } = useSubscriptions()
  const { project, dataProject, dataset, spatialExtensionConnection, accountReady } = useAccountSpatialExtensionInfo()
  const [retrying, setRetrying] = useState(false)

  const isReadyToRequestSubscriptions =
    spatialExtensionConnection && subscriptionsStatus === RequestStatus.Idle && accountReady

  const job = useMemo(() => {
    if (currentExportingJob) {
      return currentExportingJob
    } else if (retrying) {
      return
    } else if (jobId) {
      return exportingJobs.find((job) => job.jobId === jobId)
    }
  }, [exportingJobs, currentExportingJob, retrying, jobId])

  const loading = !subscription

  const handleClose = (status?: JobStatusType) => {
    clearCurrentExportingJob()
    if (status === JobStatusType.Failure) {
      setRetrying(true)
    } else {
      setRetrying(false)
      onClose()
    }
  }

  useEffect(() => {
    if (isReadyToRequestSubscriptions)
      dispatch(
        fetchSubscriptionsRequest({
          connection: spatialExtensionConnection?.name as string,
          dataset,
          project,
          dataProject
        })
      )
  }, [dispatch, spatialExtensionConnection, dataset, project, dataProject, isReadyToRequestSubscriptions])

  return (
    <Dialog open={open} size="medium" height="auto">
      <DialogHeader title={<FormattedMessage id="data-explorer.subscription.exportDialog.title" />} onClose={onClose} />
      {loading ? (
        <DialogContent>
          <Box py={10}>
            <Loader />
          </Box>
        </DialogContent>
      ) : (
        <SubscriptionExportDialogContent table={table} job={job} onClose={handleClose} />
      )}
    </Dialog>
  )
}

export default SubscriptionExportDialog
